import { useMutation, useQueryClient } from '@tanstack/react-query'
import { gtmWorkspaceKeys } from '../gtm-workspace-queries'
import type { PaymentStatusResponse } from '@laplace/contracts'
import {
  billingKeys,
  paymentsConfigQueryOptions,
} from '@/features/billing/api/billing-queries'
import {
  payCheckoutSession,
  waitForConfirmation,
} from '@/features/billing/domain/payment-checkout'
import { useBootstrapQuery } from '@/features/bootstrap/api/bootstrap/queries/bootstrap-query'
import {
  trackGtmEvent,
  trackGtmPurchase,
} from '@/features/gtm/domain/gtm-analytics'
import { apiClient } from '@/shared/api/client'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'

export interface GtmTopUpResult {
  payment: PaymentStatusResponse
  /** False: the provider took the payment, the server has not confirmed yet. */
  confirmed: boolean
}

/**
 * In-app top-up: starts a payment for the organization, takes the owner
 * through the provider checkout (test payments complete directly), waits for
 * the server to confirm it and refreshes every balance on screen.
 * `onProviderCheckout` reports while the provider's overlay is open (the
 * calling dialog hides meanwhile; see payCheckoutSession).
 */
export function useGtmTopUp(
  workspaceId: string,
  options: { onProviderCheckout?: (open: boolean) => void } = {},
) {
  const queryClient = useQueryClient()
  const lang = useCurrentLang()
  const bootstrap = useBootstrapQuery()
  const organizationId = bootstrap.data?.organization?.id ?? null

  const mutation = useMutation({
    mutationFn: async (amountUsd: number): Promise<GtmTopUpResult> => {
      if (!organizationId) throw new Error('No organization')
      const config = await queryClient.fetchQuery(paymentsConfigQueryOptions())
      trackGtmEvent('begin_checkout', {
        currency: 'USD',
        value: amountUsd,
        amount_usd: amountUsd,
        payment_provider: config.provider,
        checkout_context: 'app',
      })
      const session = await apiClient.billing.startTopUp(organizationId, {
        amountUsd,
      })
      await payCheckoutSession(session, config, {
        completeTestPayment: () =>
          apiClient.billing.fakeCompleteTopUp(
            organizationId,
            session.paymentId,
          ),
        email: bootstrap.data?.user.email,
        locale: lang,
        onProviderCheckout: options.onProviderCheckout,
      })
      const { value, done } = await waitForConfirmation(
        () => apiClient.billing.topUp(organizationId, session.paymentId),
        (payment) => payment.status === 'completed',
      )
      return { payment: value, confirmed: done }
    },
    onSuccess: ({ payment, confirmed }) => {
      if (!confirmed) return
      if (payment.isTest) {
        trackGtmEvent(
          'gtm_test_payment_completed',
          { payment_provider: payment.provider, checkout_context: 'app' },
          payment.id,
        )
      } else {
        trackGtmPurchase({
          paymentId: payment.id,
          amountCents: payment.amountCents,
          provider: payment.provider,
          isTest: payment.isTest,
          status: payment.status,
          context: 'app',
        })
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: gtmWorkspaceKeys.all(workspaceId),
        }),
        queryClient.invalidateQueries({ queryKey: billingKeys.all }),
      ])
    },
  })

  return { ...mutation, organizationId }
}
